/**
 * What happens when a page's code fails to download.
 *
 * Usually a stale index or cache after a deploy: one hard navigation to the same address pulls
 * fresh assets and heals it. That reload is guarded per address so it cannot loop. But once the
 * guard was spent, a second failure did nothing at all, and the first navigation after a reload
 * renders nothing when it fails, so the tab went blank with an empty accessibility tree (UI/UX
 * audit F06, 3 Nov 2026). Now a spent guard shows PageLoadFailed instead, with Try again and Back.
 *
 * The guard is cleared once the address has loaded, so a later failure in the same tab gets its
 * own self-healing reload rather than inheriting a guard spent hours ago.
 */
import { ref } from 'vue'

const CHUNK_ERROR = /dynamically imported module|Importing a module script failed|error loading dynamically imported|Failed to fetch/i

/** Set while a page could not be loaded: `{ path }`. Rendered by PageLoadFailed in App.vue. */
export const pageLoadFailure = ref(null)

export function isChunkLoadError(error) {
    return CHUNK_ERROR.test(String(error?.message || ''))
}

export function guardKey(path) {
    return 'reload:chunk:' + path
}

/**
 * 'reload' when a one-off hard navigation should heal it, 'show' when that has already been
 * tried for this address. Storage that refuses access allows the reload, as before.
 */
export function decideChunkRecovery(storage, path) {
    const key = guardKey(path)
    try {
        if (storage.getItem(key)) return 'show'
        storage.setItem(key, '1')
    } catch (_) { /* storage blocked: allow one navigation */ }
    return 'reload'
}

export function clearChunkGuard(storage, path) {
    try { storage.removeItem(guardKey(path)) } catch (_) { /* storage blocked: nothing to clear */ }
}
